import React, { useEffect, useRef } from 'react';
import {
  ArrayInput,
  SimpleFormIterator,
  DateInput,
  TextInput,
  BooleanInput,
  NumberInput,
  required,
} from 'react-admin';
import { useFormContext, useWatch } from 'react-hook-form';
import moment from 'moment';
import { generateSessionID, calculateSessionDates } from '../../../utils/eventFormUtils';

/**
 * Session Manager Component
 * Handles session creation and management for multi-date events
 */
export const SessionManager = ({ disabled = false, isEdit = false }) => {
  return (
    <React.Fragment>
      <NumberInput
        source="duration"
        label="Event Duration (minutes)"
        fullWidth
        validate={required()}
        disabled={disabled}
      />

      <SessionDateCalculator />

      <ArrayInput source="sessions" label="Sessions" validate={required()}>
        <SimpleFormIterator
          disableReordering
          disableRemove={disabled}
          disableAdd={disabled}
          className="sessionEventSessionsArray"
        >
          <SessionDateInput source="startdate" disabled={disabled} isEdit={isEdit} />

          <ArrayInput source="times" label="Times">
            <SimpleFormIterator
              disableReordering
              disableRemove={disabled}
              disableAdd={disabled}
              inline
            >
              <EnsureSessionTimeId source="id" />
              <TextInput
                source="id"
                label="Session ID"
                validate={required()}
                fullWidth
                InputProps={{ readOnly: true }}
              />
              <TextInput
                source="time"
                fullWidth
                validate={required()}
                label="Time"
                type="time"
                disabled={disabled}
                InputProps={{
                  inputProps: {
                    step: 300, // 5 min
                  },
                }}
              />
              <BooleanInput
                source="isActive"
                label="Active"
                defaultValue={true}
                disabled={disabled}
              />
            </SimpleFormIterator>
          </ArrayInput>
        </SimpleFormIterator>
      </ArrayInput>
    </React.Fragment>
  );
};

/** Iterator satırına session time id yazar (disabled/defaultValue kaçmasın diye). */
const EnsureSessionTimeId = ({ source }) => {
  const { setValue, getValues } = useFormContext();
  useEffect(() => {
    if (!source) return;
    const current = getValues(source);
    if (!current) {
      setValue(source, generateSessionID(), { shouldDirty: false });
    }
  }, [source, getValues, setValue]);
  return null;
};

/**
 * SimpleFormIterator source prefix'ini iletmek için source prop zorunlu.
 * Yoksa DateInput root `startdate`'e yazar → sessions[i].startdate boş kalır.
 * Tarih girince saate otomatik focus yok — kullanıcı kendisi seçer (session UX).
 */
const SessionDateInput = ({ disabled = false, isEdit = false, source = 'startdate', ...rest }) => {
  // YYYY-MM-DD string tut — Date/UTC kayması session tarihini bir gün kaydırır / kayıtta kaybettirir.
  const toDateString = (value) => {
    if (!value) return value;
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
      return value.slice(0, 10);
    }
    const m = moment(value);
    return m.isValid() ? m.format('YYYY-MM-DD') : value;
  };

  return (
    <DateInput
      {...rest}
      source={source}
      fullWidth
      label="Date"
      disabled={disabled}
      format={toDateString}
      parse={toDateString}
      validate={[
        required(),
        (value) => {
          if (!value) return undefined;
          if (!moment(value).isValid()) return 'Please enter a valid date.';
          if (!isEdit && moment(value).isBefore(moment(), 'day')) {
            return 'Start date should be after today.';
          }
          return undefined;
        },
      ]}
    />
  );
};

/**
 * Session Date Calculator Component
 * Auto-calculates start and end dates based on sessions
 */
const SessionDateCalculator = () => {
  const { setValue } = useFormContext();
  const sessions = useWatch({ name: 'sessions' });
  const lastCalculatedRef = useRef(null);

  useEffect(() => {
    if (sessions && sessions.length > 0) {
      // Only recalculate if sessions have actually changed
      const sessionsString = JSON.stringify(sessions);
      if (lastCalculatedRef.current !== sessionsString) {
        const { startdate, enddate } = calculateSessionDates(sessions);
        if (startdate) setValue('startdate', startdate, { shouldDirty: true });
        if (enddate) setValue('enddate', enddate, { shouldDirty: true });
        lastCalculatedRef.current = sessionsString;
      }
    }
  }, [sessions, setValue]);

  return null; // This component doesn't render anything
};
